/**
 * src/state/rewrittenMessages.js
 *
 * Plain-language sentences for the reasons state-write gives when it stores
 * something other than what was sent (the response's `rewritten` field —
 * azure-functions/shared/state/rewritten.js, OPP004A-04, GH #7167).
 *
 * One table for every rule a guard can report, so the person is told what
 * happened to their change and what to do next, naming the record — never a
 * rule id. An unknown rule still produces a sentence: a guard that learns a
 * new rule ahead of this bundle must fail towards telling the user.
 */

const q = (s) => `“${s}”`;

const MR_RULES = {
  'complete-needs-owner': (a) => `${q(a)} was not marked complete: give it an owner first.`,
  'complete-needs-evidence': (a) =>
    `${q(a)} was not marked complete: attach at least one piece of evidence from the Artefact register.`,
  'start-needs-owner': (a) => `${q(a)} was not started: give it an owner first.`,
  'cancel-needs-reason': (a) => `${q(a)} was not cancelled: a reason is required.`,
  'owner-cleared': (a) =>
    `The owner of ${q(a)} was kept: work that has started cannot be left without an owner.`,
  'evidence-removed': (a) =>
    `The evidence on ${q(a)} was kept: completed work must keep evidence that exists in the Artefact register.`,
  'action-removed': (a) =>
    `${q(a)} was put back: an action on a management review is cancelled with a reason, never deleted.`,
  'verification-log-edited': (a) =>
    `The verification history of ${q(a)} was kept as recorded: past verifications cannot be changed.`,
  'verification-not-completed': (a) =>
    `The verification of ${q(a)} was not recorded: only a completed action can be verified.`,
  'verification-same-save': (a) =>
    `The verification of ${q(a)} was not recorded: save the completion first, then verify effectiveness.`,
  'verification-invalid': (a) =>
    `A verification of ${q(a)} was not recorded: choose effective, not effective or not yet.`,
  'follow-up-link-changed': (a) =>
    `${q(a)} still follows up the action it was raised for: that link cannot be changed.`,
  'follow-up-link-invalid': (a) =>
    `${q(a)} was saved, but not as a follow-up: the action it named has no not-yet or not-effective verdict to follow up.`,
  'target-frozen': (a) =>
    `The improvement target of ${q(a)} was kept: it is fixed once work has started.`,
};

function findById(list, id) {
  return (Array.isArray(list) ? list : []).find((x) => x && String(x.id) === String(id));
}

function mrSentence(reason, reviews) {
  const review = findById(reviews, reason.reviewId);
  const reviewName = review?.title || 'a management review';
  if (reason.rule === 'review-delete-refused') {
    return `The management review ${q(reviewName)} was kept: a review with recorded actions, decisions or a snapshot cannot be deleted.`;
  }
  if (reason.rule === 'msr-pin-unavailable') {
    return `The management review ${q(reviewName)} was not started: the measurement it rests on could not be read, and your other changes to this review were not saved. Try again.`;
  }
  if (reason.rule === 'doubt-loop-pin-unavailable') {
    return review?.status === 'complete'
      ? `The doubt-loop input for ${q(reviewName)} could not be captured. Your changes were saved; the review shows that this input was not captured.`
      : `The doubt-loop input for ${q(reviewName)} could not be captured yet. Your changes were saved; it is captured on the next save while the review is open.`;
  }
  if (reason.rule === 'doubt-loop-pin-server-owned') {
    return `The doubt-loop input of ${q(reviewName)} was kept: it is recorded by the server and never changes.`;
  }
  if (reason.rule === 'leader-dialogue-pin-server-owned') {
    return `The leader-dialogue inputs of ${q(reviewName)} were kept as recorded: they are written by the server and never change.`;
  }
  if (reason.rule === 'msr-pin-server-owned') {
    return `The measurement close ${q(reviewName)} rested on was kept: it is recorded by the server when the review starts and never changes.`;
  }
  const action = findById(review?.actions, reason.actionId);
  const actionName = action?.title || action?.description || 'An action';
  const fn = MR_RULES[reason.rule];
  return fn
    ? fn(actionName)
    : `The server changed what was saved for ${q(actionName)} on ${q(reviewName)}. Open it to check what was kept.`;
}

// A guard that hands back the stored record whole drops every other edit to
// it in the same save; one that may do so says that it may.
const TASK_LOST = 'Your other changes to this task were not saved either: make them again.';
const MAY_LOSE = 'Other changes made to it in this save may not have been kept: check it.';
// competenceGapTask.cjs header: Met with evidence, a qualifying training
// completion, or the requirement leaving the employee — never Not Met.
const GAP_CLOSES =
  'a competence-gap task closes when an assessment with evidence shows the requirement met, a qualifying training is completed, or the requirement no longer applies to the employee.';

// The other restore-don't-reject guards in state-write — one record per id.
const OTHER_RULES = {
  'ownership-ack-kept': (n) =>
    `The owner acknowledgement on control ${q(n)} was kept as recorded: it is given only by the control owner.`,
  'next-review-kept': (n) =>
    `The next review date of ${q(n)} was kept: a published document's review date follows its review cycle.`,
  'incident-fields-kept': (n) =>
    `The times, severity or root-cause links of incident ${q(n)} were kept as recorded.`,
  'asset-criticality-derived': (n) =>
    `The criticality of asset ${q(n)} was kept: it is derived from its business impact analysis.`,
  'backup-test-log-kept': (n) =>
    `The test log of backup ${q(n)} was kept as recorded: past tests cannot be changed.`,
  'governance-rbac-kept': () =>
    'Governance RBAC was kept as it was: only an administrator can change it, and opting out needs a reason and a review date.',
  'governance-rbac-step-up': () =>
    'Governance RBAC was kept on: opting out needs you to confirm it is you first. Try again and complete the verification.',
  'consistency-disable-needs-reason': () =>
    'The source consistency check was kept on: turning it off needs a reason of at least 10 characters.',
  // Required competence on a role (RCQA-02, roleCompetence.cjs
  // stampCompetenceHistory and state-write's role-removal guard).
  'competence-requirements-admin-only': (n) =>
    `The required competence of role ${q(n)} was kept as it was: only an administrator can change it.`,
  'competence-requirements-invalid': (n) =>
    `The required competence of role ${q(n)} was kept as it was: one of the requirements was not valid. Open the role and check each requirement.`,
  // roleCompetence release flag (RFLG-B-006, state-write): `unconfirmed` on
  // the pre-commit refusal when the release could not be read rather than
  // read OFF.
  'competence-requirements-not-released': (n, r) =>
    `The new required competence on role ${q(n)} was not saved: ${
      r.unconfirmed
        ? 'the release of role competence could not be confirmed. Try again shortly.'
        : 'role competence is not released to your organisation.'
    } Your other changes to this role were saved.`,
  // Taken back only behind a cutover or legacy switch-off; an ordinary one is
  // undone by the record-holder floor instead (GH #8917).
  'competence-requirements-rolled-back': (n, r) =>
    `The required competence you just added to role ${q(n)} was taken back: role competence was switched off for your organisation while you saved.${
      r.logged === false ? '' : ' This is recorded in the activity log.'
    }`,
  'competence-requirements-rollback-failed': (n, r) =>
    `The required competence you just added to role ${q(n)} was saved, but role competence was switched off for your organisation while you saved, and it could not be taken back.${
      r.logged === false
        ? ' It could not be recorded in the activity log either: note it, and remove the requirement if role competence should not be in use.'
        : ' This is recorded in the activity log.'
    }`,
  // The record-holder floor (GH #8917) could not settle after the add landed:
  // the requirement is kept and the floor sweeper checks again.
  'competence-requirements-floor-unsettled': (n, r) =>
    `The required competence you just added to role ${q(n)} was saved, but whether role competence stays visible to your organisation could not be confirmed. It is checked again automatically within the hour.${
      r.logged === false ? '' : ' This is recorded in the activity log.'
    }`,
  'competence-history-server-owned': (n) =>
    `The competence history of role ${q(n)} was kept as recorded: it is written by the server and past entries cannot be changed.`,
  'competence-role-admin-only': (n, r) =>
    r.id === '*'
      ? 'Your role changes were not saved: a role that carries required competence or its history can only be changed or removed by an administrator.'
      : `Role ${q(n)} was put back: a role that carries required competence or its history can only be removed by an administrator.`,
  // The stored exercise comes back whole; a legacy one may already be
  // completed without an outcome, and then nothing in the save was kept.
  'exercise-outcome-required': (n, _r, rec) =>
    rec?.status === 'completed'
      ? `Your changes to exercise ${q(n)} were not saved: a completed exercise needs its outcome recorded first. Record the outcome, then make your changes again.`
      : `Exercise ${q(n)} was not marked completed: record its outcome first. ${MAY_LOSE}`,
  'bia-stamps-kept': (n) =>
    `Who completed and approved business impact analysis ${q(n)}, and when, was kept as recorded: the server sets it.`,
  'risk-origin-kept': (n) =>
    `Where risk ${q(n)} came from was kept as recorded: a risk's origin is set when it is created and never changes.`,
  'risk-ai-settings-server-only': () =>
    'The AI settings for risk assessment were kept as they were: they cannot be changed by this save.',
  'risk-method-server-only': () =>
    'The risk method was kept as it was: it cannot be changed by this save.',
  'gap-report-server-only': (n) =>
    `The report of gap assessment ${q(n)} was kept as generated: it is written by the server.`,
  'soa-versions-kept': () =>
    'Your Statement of Applicability versions were kept: they cannot be removed by a save.',
  'soa-approval-immutable': () =>
    'An approved Statement of Applicability version was kept as approved: an approved version cannot be changed or removed.',
  'soa-approve-server-only': () =>
    'A Statement of Applicability version was saved without its approval: a version is approved only through the approval step.',
  'soa-snapshot-kept': () =>
    'The controls of a Statement of Applicability version awaiting approval were kept: what the approver sees cannot change. Save a new version instead.',
  'obligation-enable-stamp-kept': () =>
    'When and by whom an obligation was enabled was kept as recorded.',
  'obligation-enable-server-only': () =>
    'An obligation was not enabled by this save: enable it from the obligation itself.',
  'release-flags-server-only': () =>
    'Release settings were kept as they were: only the platform administrator can change them.',
  'employee-manager-admin-only': (n) =>
    `The line manager and linked user of ${q(n)} were kept as they were: only an administrator can change them.`,
  // Ad-hoc task closures (GH #7704, shared/tasks/closure.cjs).
  'closures-append-only': (n) =>
    `The closure history of task ${q(n)} was kept as recorded: past closures cannot be changed or removed.`,
  'closure-entry-without-close': (n) =>
    `A closure note on task ${q(n)} was not saved: a closure is recorded only when the task is closed.`,
  'scan-suggestions-undecided': (n) =>
    `Task ${q(n)} was not closed: decide every document-scan suggestion first. ${TASK_LOST}`,
  'unknown-status': (n) =>
    `The status of task ${q(n)} was not changed: the status sent is not one a task can have. ${MAY_LOSE}`,
  'close-needs-closure-record': (n) =>
    `Task ${q(n)} was not closed: closing a task needs a complete closure record. ${MAY_LOSE} Close it again and fill in the closure details.`,
  // Competence-gap tasks are the server's (RCQA-04, competenceGapTask.cjs).
  'competence-gap-created-by-client': () =>
    'A competence-gap task was not created: these tasks are raised by the server when an employee lacks a required competence.',
  'competence-gap-retyped-by-client': (n) =>
    `Task ${q(n)} was kept as it was: an existing task cannot be turned into a competence-gap task.`,
  'competence-gap-closed-by-hand': (n) => `Task ${q(n)} was not closed: ${GAP_CLOSES} ${TASK_LOST}`,
  'competence-gap-reopened-by-hand': (n) =>
    `Task ${q(n)} was not reopened: a competence-gap task is opened and closed only by the server, from the employee's competence assessments. ${TASK_LOST}`,
  'competence-gap-server-owned-fields': (n) =>
    `The fields the server sets on competence-gap task ${q(n)} were kept as recorded.`,
  'competence-gap-deleted-by-client': (n) =>
    `Task ${q(n)} was put back: a competence-gap task cannot be deleted. ${GAP_CLOSES}`,
  'audit-completion-blocked': (n, r) =>
    `Audit ${q(n)} was not completed${
      Array.isArray(r.blockers) && r.blockers.length ? `: ${r.blockers.join(' ')}` : '.'
    }`,
};
// Rules whose id carries the values it was given (closure.cjs).
const PATTERN_RULES = [
  [
    /^transition-/,
    (n) =>
      `The status of task ${q(n)} was not changed: a task cannot move directly between those statuses. ${TASK_LOST}`,
  ],
];

// A rule this bundle has no sentence for. It does not know what the guard
// kept, so it never claims the change was lost — only that the server
// changed what was saved.
const unknownSentence = (name) =>
  `The server changed what was saved for ${q(name)}. Open it to check what was kept.`;

function otherSentence(reason, value) {
  const id = reason.id ?? reason.taskId;
  const rec = Array.isArray(value) ? findById(value, id) : null;
  const name = rec?.title || rec?.name || id || 'a record';
  const fn = OTHER_RULES[reason.rule] || PATTERN_RULES.find(([re]) => re.test(reason.rule))?.[1];
  return fn ? fn(name, reason, rec) : unknownSentence(name);
}

function cancelSentence(key, reason, records) {
  const parent = findById(records, reason.parentId);
  const action = findById(parent?.actions, reason.actionId);
  const name = action?.title || 'An action';
  if (reason.rule === 'cancel-needs-reason')
    return `${q(name)} was not cancelled: a reason is required.`;
  return unknownSentence(name);
}

/**
 * @param {Record<string, { value: unknown, reasons: object[] }>} rewritten
 * @returns {string[]} one sentence per reason, in order; empty when none
 */
export function describeRewritten(rewritten) {
  const out = [];
  for (const [key, entry] of Object.entries(rewritten || {})) {
    for (const reason of entry?.reasons || []) {
      if (!reason) continue;
      if (key === 'managementReviews') out.push(mrSentence(reason, entry.value));
      else if (reason.parentId) out.push(cancelSentence(key, reason, entry.value));
      else out.push(otherSentence(reason, entry.value));
    }
  }
  return out;
}
